<script setup lang="ts">
import { DRAG_HANDLE_CLASS } from '@/composables/jobEdit/useJobEditSectionsOrder';

export interface Props {
  title?: string;
  collapsed?: boolean;
  isEditing?: boolean;
  isLoading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  collapsed: false,
  isEditing: false,
  isLoading: false,
});

const emit = defineEmits<{
  click: [event: Event];
  cancel: [];
  save: [];
}>();

function handleClick(event: Event): void {
  emit('click', event);
}

function handleChevronClick(event: Event): void {
  event.stopPropagation();
  emit('click', event);
}

function handleCancel(event: Event): void {
  event.stopPropagation();
  emit('cancel');
}

function handleSave(event: Event): void {
  event.stopPropagation();
  emit('save');
}
</script>

<template>
  <div :class="$style.jobEditSectionHeader" data-testid="jobEditSectionHeader">
    <div :class="$style.leftSection" @click="handleClick">
      <v-icon
        v-show="!props.isEditing"
        :class="[DRAG_HANDLE_CLASS, $style.dragHandle]"
        size="20"
        @click.stop
        >mdi-drag-vertical</v-icon
      >
      <h3 :class="$style.title">{{ props.title }}</h3>
      <div v-if="props.collapsed" :class="$style.collapsedContent">
        <slot name="collapsedContent" />
      </div>
    </div>

    <div :class="$style.rightSection">
      <template v-if="props.isEditing">
        <v-btn
          variant="text"
          size="small"
          :disabled="props.isLoading"
          @click="handleCancel"
        >
          CANCEL
        </v-btn>
        <v-btn
          variant="flat"
          color="primary"
          size="small"
          :loading="props.isLoading"
          @click="handleSave"
        >
          SAVE
        </v-btn>
      </template>
      <slot name="actions" />
      <v-icon :class="$style.chevron" size="20" @click="handleChevronClick">
        {{ props.collapsed ? 'mdi-chevron-down' : 'mdi-chevron-up' }}
      </v-icon>
    </div>
  </div>
</template>

<style module lang="scss">
@use '@/styles/colors';

.jobEditSectionHeader {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  height: 48px;
  user-select: none;
  position: sticky;
  top: var(--page-header-height, 0px);
  z-index: 10;
  background-color: colors.$color-gray-50;
  border-radius: 8px 8px 0 0;
  border-bottom: 1px solid colors.$color-gray-200;
}

.leftSection {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  flex: 1;
  padding: 4px 0;
  margin: -4px 0;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: colors.$color-gray-50;
  }
}

.dragHandle {
  color: colors.$color-text-tertiary;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
}

.title {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: colors.$color-text-secondary;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.rightSection {
  display: flex;
  align-items: center;
  gap: 8px;
}

.collapsedContent {
  display: flex;
  align-items: center;
  gap: 8px;
  color: colors.$color-text-tertiary;
  font-size: 0.875rem;
}

.chevron {
  color: colors.$color-text-secondary;
  cursor: pointer;
  padding: 4px;
  margin: -4px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: colors.$color-gray-100;
    border-radius: 4px;
  }
}
</style>
